import * as DialogPrimitive from "@radix-ui/react-dialog"

import * as React from "react"

import { MOTION_CLASSES } from "@/lib/motion"
import { cn } from "@/lib/utils"
import { semanticTokens } from "@packages/config/theme"
import { sfIcons } from '@/lib/sf-icons';
import { SFIcon } from '@/components/ui/sf-icon';

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // Liquid Glass Backdrop (CANON v1.4.5 - Modal Tier)
      "fixed inset-0 z-50",
      // Light mode: subtle dark overlay
      "bg-black/30",
      // Dark mode: deeper overlay for contrast (using semantic token)
      "dark:bg-background/60",
      "backdrop-blur-xl backdrop-saturate-[180%]",
      "dark:backdrop-saturate-150",
      MOTION_CLASSES.fadeIn,
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  /**
   * Accessible summary announced with the dialog title. Provide when no custom
   * <DialogDescription> is rendered in children so aria-describedby is set automatically.
   */
  description?: React.ReactNode
  /**
   * Optional props for the generated DialogPrimitive.Description when using the description shortcut.
   */
  descriptionProps?: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, description, descriptionProps, ...props }, ref) => {
  const generatedId = React.useId()
  const { ["aria-describedby"]: ariaDescribedByProp, ...restContentProps } = props
  const descriptionId = descriptionProps?.id ?? generatedId
  const ariaDescribedBy = ariaDescribedByProp ?? (description ? descriptionId : undefined)
  const { className: descriptionClassName, ...restDescriptionProps } = descriptionProps ?? {}

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 p-6",
          // Liquid Glass L1 Baseline (CANON v1.4.3 Section 5.4b): glass-standard
          "rounded-[32px]",
          semanticTokens.glass.modal.background,
          semanticTokens.glass.dark.modal.background,
          semanticTokens.glass.modal.blur,
          "border border-white/20 dark:border-white/10",
          "shadow-[inset_0_1px_0_rgba(255,255,255,0.1),0_25px_60px_-12px_hsl(220_15%_5%/0.30)]",
          "dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_25px_60px_-12px_hsl(0_0%_0%/0.6)]",
          MOTION_CLASSES.scaleIn,
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
          "relative overflow-hidden",
          className
        )}
        aria-describedby={ariaDescribedBy}
        {...restContentProps}
      >
        {/* Specular highlight (top edge reflection) — Liquid Glass signature (L2 Enhanced) */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/20 rounded-t-[32px]" />

        {/* Content container (relative z-10 to be above specular highlight) */}
        <div className="relative z-10">
          {description ? (
            <DialogPrimitive.Description
              id={descriptionId}
              className={cn("text-sm text-muted-foreground", descriptionClassName)}
              {...restDescriptionProps}
            >
              {description}
            </DialogPrimitive.Description>
          ) : null}
          {children}
        </div>
        <DialogPrimitive.Close
          className={cn(
            "absolute right-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-sm opacity-70 ring-offset-background hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground",
            MOTION_CLASSES.micro
          )}
        >
          <SFIcon icon={sfIcons.close} className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger
}

